import { Grid, GridColumn, GridRow } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { AppointmentForm } from '../../../components/AppointmentForm/AppointmentForm';
import { BpCalendar } from '../../../components/BpCalendar/BpCalendar';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { EventsList } from '../../../components/EventsList/EventsList';
import { useGroupCalendarEvents } from '../../../hooks/useCalendarEvents';
import { useGroupMembersCount } from '../../../hooks/useGroupMembersCount';
import { findClosestEventToNow } from '../../../utils/dateCalculations';

const GroupAppointmentsSubpagePageQueryDocument = graphql(`
  query GroupAppointmentsSubpagePageData($groupUuid: ID!) {
    ...UseGroupMembersCountFields
  }
`);

export const GroupAppointmentsSubpage: FC = () => {
  const navigate = useNavigate();

  const { uuid, groupUuid, add, edit } = useParams<{
    groupUuid: string;
    uuid: string;
    add: string;
    edit: string;
  }>();

  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data }] = useQuery({
    query: GroupAppointmentsSubpagePageQueryDocument,
    variables: { profile: profileUuid, groupUuid: groupUuid! },
    pause: groupUuid === undefined,
  });
  const perms = usePermissionChecker();
  const { organizationUuid } = useGroupMembersCount(data);

  const isAddEditAllowed = perms?.canCreateBpGroupEvents({
    uuid: groupUuid ?? '',
    organization: { uuid: organizationUuid },
  });

  const { events, refetch } = useGroupCalendarEvents(groupUuid ?? '');
  const [scrollUuid, setScrollUuid] = useState<string | null>(findClosestEventToNow(events)?.uuid ?? null);
  const [randomRedrawString, setRandomRedrawString] = useState<string>('');

  const showForm = useMemo(() => {
    return (isAddEditAllowed && (add || edit)) ?? false;
  }, [isAddEditAllowed, add, edit]);

  const appointmentUuid = useMemo(() => {
    return uuid ?? false;
  }, [uuid]);

  return (
    <BpSubpage>
      {showForm && groupUuid ? (
        <AppointmentForm
          holderUuid={groupUuid}
          organizationUuid={organizationUuid}
          conferenceContext='GroupOrCourse'
          canAssignTeachingUnit={false}
          disableEventTypeValues={['lesson', 'exam']}
          appointmentUuid={appointmentUuid ? appointmentUuid : undefined}
          onClose={() => {
            navigate(`/groups/${groupUuid}/appointments/`);
            refetch();
          }}
        />
      ) : (
        <Grid>
          <GridRow mobileGap='var(--grid-column-gap)'>
            <GridColumn width={4}>
              <BpCard className='pb-6 pr-6 pl-6' noPadding showBorder hideBackground>
                <BpCalendar
                  events={events}
                  showAddButton={false}
                  eventClick={(uuid) => {
                    setRandomRedrawString(Date.now().toString());
                    setScrollUuid(uuid);
                  }}
                />
              </BpCard>
            </GridColumn>
            <GridColumn width={8}>
              <EventsList
                courseUuid={groupUuid}
                fullHeight
                showHeader
                showCompactEvents={false}
                events={events}
                scrollToUuid={scrollUuid}
                randomRedrawString={randomRedrawString}
                onAddClick={() => {
                  navigate(`/groups/${groupUuid}/appointments/create`);
                }}
                onEdit={
                  isAddEditAllowed
                    ? (uuid) => {
                        navigate(`/groups/${groupUuid}/appointments/edit/${uuid}`);
                      }
                    : undefined
                }
              />
            </GridColumn>
          </GridRow>
        </Grid>
      )}
    </BpSubpage>
  );
};
